import React from 'react';
import SegmentationSection from './SegmentationSection';
import ICPResultAdapter from './ICPResultAdapter';
import { detectSegmentationStructure } from 'utils/structureUtils';

/**
 * Adapter component that detects the segmentation data structure and loads the appropriate component
 *
 * Old structure: { "Segments": [...] }
 * New structure: { "final_segments": [...] }
 */
const SegmentationAdapter = ({
  segmentation,
  isProUser,
  handleTrialClick,
  handleScrollToICP,
  taskId,
  analysisData,
  hoveredItems,
  setHoveredItems
}) => {
  // Use the utility function to detect data structure
  const structureType = detectSegmentationStructure(segmentation);

  // If structure is unknown, show error or fallback
  if (structureType === 'unknown') {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <p>Unable to determine segmentation data structure</p>
      </div>
    );
  }

  // For old structure, use the existing SegmentationSection
  if (structureType === 'old') {
    return (
      <SegmentationSection
        segmentation={segmentation}
        isProUser={isProUser}
        handleTrialClick={handleTrialClick}
        handleScrollToICP={handleScrollToICP}
        taskId={taskId}
        analysisData={analysisData}
        hoveredItems={hoveredItems}
        setHoveredItems={setHoveredItems}
      />
    );
  }

  // For new structure OR no data, use the ICPResultAdapter component
  // This allows ICPResultAdapter to show its own loading state when no data
  if (structureType === 'new' || structureType === 'none') {
    // Transform new structure to match ICPResultAdapter expectations
    const transformedSegments =
      structureType === 'new'
        ? segmentation.final_segments.map((segment, index) => ({
            ...segment,
            // Ensure we have the required fields for ICPResultAdapter
            segment_id: segment.segment_id || `segment_${index + 1}`,
            name: segment.name || 'Unnamed Segment',
            customer_type: segment.customer_type || 'B2C'
          }))
        : []; // Empty array when no data

    // For the new structure, the ICP data might already be available in the segments
    // Check if we have ICP data in the segments
    const segmentsWithICP = transformedSegments.map((segment) => {
      // Check if this segment has ICP data directly embedded
      if (segmentation && segmentation[segment.name]) {
        return {
          ...segment,
          hasICPData: true,
          icpData: segmentation[segment.name]
        };
      }
      return segment;
    });

    return (
      <div id="segmentation">
        <ICPResultAdapter segments={segmentsWithICP} taskId={segmentation?.icp_task_id || taskId} />
      </div>
    );
  }

  return null;
};

export default SegmentationAdapter;
